// Continuous PCM live-audio player (AudioWorklet).
//
// The MD300 sends 8 kHz mono audio in ~128 ms bursts. Playing each burst as a
// separate AudioBufferSourceNode makes the browser resample every chunk
// INDEPENDENTLY, which produces an audible click at every chunk boundary
// (the "tick tick" crackle users reported). This worklet instead keeps one
// ring buffer and resamples continuously across chunk boundaries, with a
// small jitter buffer, soft fade-in after (re)start and a soft decay on
// underrun instead of a hard cut.
const WORKLET_SOURCE = `
class ElvPcmPlayer extends AudioWorkletProcessor {
  constructor() {
    super();
    this.inRate = 8000;
    this.capacity = this.inRate * 6;
    this.buf = new Float32Array(this.capacity);
    this.head = 0;
    this.count = 0;
    this.frac = 0;
    this.playing = false;
    this.gain = 0;
    this.last = 0;
    this.sinceReport = 0;
    this.applyRate(this.inRate);
    this.port.onmessage = (e) => {
      const msg = e.data || {};
      if (msg.type === 'push' && msg.samples) this.push(msg.samples, msg.rate);
      else if (msg.type === 'reset') {
        this.head = 0; this.count = 0; this.frac = 0;
        this.playing = false; this.gain = 0;
      }
    };
  }
  applyRate(rate) {
    this.inRate = rate;
    this.startThreshold = Math.round(rate * 0.12);
    this.maxBuffered = Math.round(rate * 1.2);
    this.targetBuffered = Math.round(rate * 0.25);
  }
  push(samples, rate) {
    if (rate && rate !== this.inRate) this.applyRate(rate);
    const len = samples.length;
    if (!len || len >= this.capacity) return;
    if (this.count + len > this.capacity) {
      const drop = this.count + len - this.capacity;
      this.head = (this.head + drop) % this.capacity;
      this.count -= drop;
      this.gain = 0;
    }
    const w = (this.head + this.count) % this.capacity;
    const firstPart = Math.min(len, this.capacity - w);
    this.buf.set(samples.subarray(0, firstPart), w);
    if (firstPart < len) this.buf.set(samples.subarray(firstPart), 0);
    this.count += len;
    if (this.count > this.maxBuffered) {
      // Latency crept up (device clock faster / burst backlog) — jump ahead
      // and mask the seam with a fade-in.
      const drop = this.count - this.targetBuffered;
      this.head = (this.head + drop) % this.capacity;
      this.count -= drop;
      this.frac = 0;
      this.gain = 0;
    }
  }
  process(inputs, outputs) {
    const out = outputs[0] && outputs[0][0];
    if (!out) return true;
    const step = this.inRate / sampleRate;
    let i = 0;
    if (!this.playing && this.count >= this.startThreshold) {
      this.playing = true;
      this.gain = 0;
    }
    if (this.playing) {
      for (; i < out.length; i += 1) {
        if (this.count < 2) { this.playing = false; break; }
        const a = this.buf[this.head];
        const b = this.buf[(this.head + 1) % this.capacity];
        let v = a + (b - a) * this.frac;
        if (this.gain < 1) this.gain = Math.min(1, this.gain + 0.0015);
        v *= this.gain;
        out[i] = v;
        this.last = v;
        this.frac += step;
        while (this.frac >= 1) {
          this.frac -= 1;
          this.head = (this.head + 1) % this.capacity;
          this.count -= 1;
        }
      }
    }
    for (; i < out.length; i += 1) {
      // Underrun / not started: decay softly to silence — no hard cut.
      this.last *= 0.94;
      out[i] = this.last;
    }
    this.sinceReport += out.length;
    if (this.sinceReport >= sampleRate / 4) {
      this.sinceReport = 0;
      this.port.postMessage({
        type: 'level',
        bufferedMs: Math.round((this.count / this.inRate) * 1000),
        playing: this.playing,
      });
    }
    return true;
  }
}
registerProcessor('elv-pcm-player', ElvPcmPlayer);
`;

let workletBlobUrl = null;
const registeredContexts = new WeakSet();

export async function createPcmPlayerNode(context) {
  if (!context || !context.audioWorklet || typeof AudioWorkletNode === 'undefined') return null;
  if (!registeredContexts.has(context)) {
    if (!workletBlobUrl) {
      workletBlobUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
    }
    await context.audioWorklet.addModule(workletBlobUrl);
    registeredContexts.add(context);
  }
  return new AudioWorkletNode(context, 'elv-pcm-player', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
  });
}
